import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Header from './Header';
import Sidebar from './Sidebar';
import StatusBar from './StatusBar';

const Layout = ({ children }) => {
  const location = useLocation();
  const hideNavSidebar = location.pathname.startsWith('/maps');
  const isMapRoute = hideNavSidebar;

  const initialIsMobile = window.innerWidth < 768;
  const [isOpen, setIsOpen] = useState(!initialIsMobile);
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [isMobile, setIsMobile] = useState(initialIsMobile);
  const [sidebarLocked, setSidebarLocked] = useState(false);

  const mainContentRef = useRef(null);
  const lockTimeoutRef = useRef(null);
  const sidebarRef = useRef(null);
  const mobileRef = useRef(initialIsMobile);

  const sidebarWidth = isOpen && sidebarExpanded ? 192 : 0;

  useEffect(() => {
    if (!isMapRoute) return undefined;
    const viewport = window.visualViewport;
    const update = () => {
      document.documentElement.style.setProperty('--app-visible-height', `${viewport?.height || window.innerHeight}px`);
    };
    update();
    window.addEventListener('resize', update);
    viewport?.addEventListener('resize', update);
    viewport?.addEventListener('scroll', update);
    return () => {
      window.removeEventListener('resize', update);
      viewport?.removeEventListener('resize', update);
      viewport?.removeEventListener('scroll', update);
      document.documentElement.style.removeProperty('--app-visible-height');
    };
  }, [isMapRoute]);

  useEffect(() => {
    const handleResize = () => {
      const nextIsMobile = window.innerWidth < 768;
      setIsMobile(nextIsMobile);

      if (mobileRef.current !== nextIsMobile) {
        setIsOpen(!nextIsMobile);
        setSidebarExpanded(true);
        mobileRef.current = nextIsMobile;
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(lockTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (isMobile) setIsOpen(false);
  }, [location.pathname, isMobile]);

  useEffect(() => {
    const t = setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
      window.dispatchEvent(new Event('layout-change'));
    }, 350);
    return () => clearTimeout(t);
  }, [isOpen, sidebarExpanded, hideNavSidebar]);

  const toggleSidebar = (e) => {
    if (hideNavSidebar) return;

    e?.preventDefault();
    e?.stopPropagation();

    setSidebarLocked(true);
    clearTimeout(lockTimeoutRef.current);

    if (isMobile) {
      setIsOpen((prev) => !prev);
    } else {
      setIsOpen(true);
      setSidebarExpanded((prev) => !prev);
    }

    lockTimeoutRef.current = setTimeout(() => {
      setSidebarLocked(false);
    }, 1000);
  };

  const closeSidebar = () => {
    if (hideNavSidebar) return;
    if (isMobile && isOpen && !sidebarLocked) {
      setIsOpen(false);
    }
  };

  const handleContentClick = (e) => {
    if (hideNavSidebar) return;
    if (sidebarLocked) return;
    if (e.target.closest('[data-sidebar-toggle]')) return;
    if (sidebarRef.current?.contains(e.target)) return;
    if (isMobile && isOpen) {
      closeSidebar();
    }
  };

  return (
    <div
      className={`flex flex-col bg-gray-100 dark:bg-gray-900 ${isMapRoute ? 'map-app-layout overflow-hidden' : 'min-h-screen'}`}
    >
      <div className="relative shrink-0 z-[999999]">
        <Header toggleSidebar={hideNavSidebar ? undefined : toggleSidebar} />
      </div>

      <div className={`flex flex-1 min-h-0 min-w-0 relative ${isMapRoute ? 'overflow-hidden' : ''}`}>
        {!hideNavSidebar && isMobile && isOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-10" onClick={closeSidebar} />
        )}

        {!hideNavSidebar && (
          <div
            ref={sidebarRef}
            className="h-full"
            style={{
              position: 'fixed',
              top: '2.5rem',
              bottom: '28px',
              left: 0,
              width: isOpen ? (sidebarExpanded ? '12rem' : '0rem') : '0',
              overflow: 'hidden',
              transition: 'width 0.3s ease-in-out',
              zIndex: 99999,
            }}
          >
            <Sidebar isOpen={isOpen} expanded={sidebarExpanded} onClose={closeSidebar} />
          </div>
        )}

        <main
          ref={mainContentRef}
          className={`flex-1 min-h-0 min-w-0 transition-all duration-300 ${isMapRoute ? 'overflow-hidden' : 'overflow-visible'}`}
          style={{
            marginLeft: !hideNavSidebar && !isMobile ? `${sidebarWidth}px` : 0,
            opacity: 1,
            padding: '0px 0px',
          }}
          onClick={handleContentClick}
        >
          {children}
        </main>
      </div>

      <div
        className="app-status-footer relative shrink-0 z-30"
        style={{
          position: isMapRoute ? 'sticky' : 'relative',
          bottom: isMapRoute ? 0 : 'auto',
          marginTop: isMapRoute ? 0 : 'auto',
        }}
      >
        <StatusBar />
      </div>
    </div>
  );
};

export default Layout;
